import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, DatePicker, Select, Tag, TextInput, Tooltip } from '@opendate-app/setlist';
import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';
import { PROMO_CODE_CHAR_PATTERN } from '../../shared/promoCodeValidation';

import {
  OTHER_PRESALE_PRESET, PRESALE_PRESETS, DOORS_NAME, kindFromName, PUBLIC_ON_SALE_NAME, followTicketName, heldCount, toInt, windowKey, windowNameFor,
  windowEndTime, windowStartTime,
} from '../utils/presaleWindows';

const DATE_AND_TIME = 'Date & Time';
const SHOW_DETAIL_BASED = 'Based on show details';
const CUSTOM = 'custom';
const PRESALE = 'presale';
const DAY_OF = 'dayOf';

const HINTS = {
  public: 'Day of show starts the minute after',
  dayOf: 'When ticket sales end',
};

const HEADINGS = {
  public: 'EDIT PUBLIC ON-SALE',
  dayOf: 'EDIT DAY OF SHOW',
};

const byAt = (a, b) => new Date(a.at) - new Date(b.at);

const toDate = (value) => (value ? new Date(value) : null);

const startMs = (draft, showActivities) => {
  if (draft.availability === DATE_AND_TIME) return draft.start_time ? new Date(draft.start_time).getTime() : null;
  const activity = showActivities.find((a) => a.id === draft.start_show_activity_id);
  return activity ? new Date(activity.at).getTime() : null;
};

const endMs = (draft, showActivities) => {
  if (draft.availability === DATE_AND_TIME) return draft.end_time ? new Date(draft.end_time).getTime() : null;
  const activity = showActivities.find((a) => a.id === draft.end_show_activity_id);
  return activity ? new Date(activity.at).getTime() : null;
};

export const validateDraft = (draft, ticketType, showActivities, startAfter = null, endBefore = null) => {
  const pool = toInt(ticketType.available);
  const others = heldCount(ticketType, windowKey(draft));
  const qty = toInt(draft.available);
  const sharing = draft.available === '' || draft.available == null || qty === pool;
  const qtyOver = pool > 0 && !sharing && qty + others > pool;
  const start = startMs(draft, showActivities);
  const end = endMs(draft, showActivities);
  const endOk = start != null && end != null && end > start;
  const endPassed = Boolean(draft.reenabling) && end != null && end <= Date.now();

  const draftStart = windowStartTime(draft, showActivities);
  const startTooEarly = Boolean(startAfter && draftStart) && new Date(draftStart) <= new Date(startAfter);
  const draftEnd = windowEndTime(draft, showActivities);
  const endTooLate = Boolean(endBefore && draftEnd) && new Date(draftEnd) >= new Date(endBefore);

  let endError = null;
  if (start != null && end != null && !endOk) endError = 'Must end after it starts';
  else if (endPassed) endError = 'Must end in the future to go back on sale';
  else if (endTooLate) endError = 'Must end before day of show ends';

  return {
    valid: Boolean(draft.name && draft.name.trim()) && start != null && endOk && !endPassed && !qtyOver && !startTooEarly && !endTooLate,
    qtyError: qtyOver ? `Only ${Math.max(pool - others, 0)} left in the ${pool} pool` : null,
    startError: startTooEarly ? 'Must start after public on-sale starts' : null,
    endError,
  };
};

const activityTime = (activity) => offsetDateForBrowser(activity.at);

const activityAtTime = (showActivities, time) => (
  time ? showActivities.find((a) => new Date(activityTime(a)).getTime() === new Date(time).getTime()) : null
);

export const splitDraft = (draft, showActivities) => {
  if (draft.availability !== DATE_AND_TIME) return { ...draft, startCustom: false, endCustom: false };
  const start = activityAtTime(showActivities, draft.start_time);
  const end = activityAtTime(showActivities, draft.end_time);
  return {
    ...draft,
    start_show_activity_id: start ? start.id : null,
    end_show_activity_id: end ? end.id : null,
    startCustom: !start,
    endCustom: !end,
  };
};

export const joinDraft = ({ startCustom, endCustom, ...draft }, showActivities) => {
  if (!startCustom && !endCustom) return { ...draft, availability: SHOW_DETAIL_BASED, start_time: null, end_time: null };
  const timeOf = (id) => {
    const activity = showActivities.find((a) => a.id === id);
    return activity ? activityTime(activity) : null;
  };
  return {
    ...draft,
    availability: DATE_AND_TIME,
    start_show_activity_id: null,
    end_show_activity_id: null,
    start_time: startCustom ? draft.start_time : timeOf(draft.start_show_activity_id),
    end_time: endCustom ? draft.end_time : timeOf(draft.end_show_activity_id),
  };
};

const PresaleWindowEditor = ({
  window: initial, ticketType, showActivities, isNew, variant, startAfter, endBefore, onSave, onCancel, onRemove,
}) => {
  const presale = variant === PRESALE;
  const [draft, setDraft] = useState(() => splitDraft(initial, showActivities));
  const baseNameRef = useRef(ticketType.base_name);
  useEffect(() => {
    const previous = baseNameRef.current;
    baseNameRef.current = ticketType.base_name;
    if (previous !== ticketType.base_name) {
      setDraft((current) => ({ ...current, name: followTicketName(current.name, previous, ticketType.base_name) }));
    }
  }, [ticketType.base_name]);
  const patch = (changes) => setDraft((current) => ({ ...current, ...changes }));
  const pool = toInt(ticketType.available);
  const joined = joinDraft(draft, showActivities);
  const { valid, qtyError, startError, endError } = validateDraft(joined, ticketType, showActivities, startAfter, endBefore);
  const customStart = useMemo(() => toDate(draft.start_time), [draft.start_time]);
  const customEnd = useMemo(() => toDate(draft.end_time), [draft.end_time]);

  const sorted = [...showActivities].sort(byAt);
  const doors = sorted.find((a) => a.name === DOORS_NAME);
  const startChoices = presale ? sorted.filter((a) => !doors || new Date(a.at) < new Date(doors.at)) : sorted;
  const startTime = windowStartTime(joined, showActivities);
  const startAt = startTime ? new Date(startTime).getTime() : null;
  const isAfter = (activity, ms) => Boolean(activity) && ms != null && new Date(activityTime(activity)).getTime() > ms;
  const endChoices = sorted.filter((a) => startAt == null || isAfter(a, startAt));
  const options = (list) => [...list.map((a) => ({ value: String(a.id), label: a.name })), { value: CUSTOM, label: 'Custom date & time…' }];

  const onSaleActivity = showActivities.find((a) => a.name === PUBLIC_ON_SALE_NAME);

  const onStart = (value) => {
    if (value === CUSTOM) return patch({ startCustom: true, start_time: windowStartTime(joined, showActivities) });
    const start = showActivities.find((a) => a.id === Number(value));
    const nextStartAt = start ? new Date(activityTime(start)).getTime() : null;
    if (draft.endCustom) return patch({ startCustom: false, start_show_activity_id: Number(value) });
    const currentEnd = showActivities.find((a) => a.id === draft.end_show_activity_id);
    let end = null;
    if (isAfter(currentEnd, nextStartAt)) end = currentEnd;
    else if (presale && isAfter(onSaleActivity, nextStartAt)) end = onSaleActivity;
    return patch({ startCustom: false, start_show_activity_id: Number(value), end_show_activity_id: end ? end.id : null });
  };

  const onEnd = (value) => {
    if (value === CUSTOM) return patch({ endCustom: true, end_time: windowEndTime(joined, showActivities) });
    return patch({ endCustom: false, end_show_activity_id: Number(value) });
  };

  const pickPreset = (preset) => {
    const start = showActivities.find((a) => a.name === preset.startName);
    patch({ kind: preset.kind, name: windowNameFor(ticketType.base_name, preset.label) });
    if (start) onStart(String(start.id));
  };

  return (
    <div className="presale-window-editor">
      <div className="presale-window-editor-heading">
        {HEADINGS[variant] || (isNew ? 'NEW PRE-SALE WINDOW · STEP 2 OF 2' : 'EDIT PRE-SALE WINDOW')}
      </div>

      {presale ? (
        <>
          <TextInput label="Window name" required name="name" value={draft.name}
            onChange={(e) => patch({ name: e.target.value })} />
          <div className="presale-window-editor-chips">
            {[...PRESALE_PRESETS, OTHER_PRESALE_PRESET].map((preset) => (
              <Tag key={preset.kind} size="sm" selectable selected={(draft.kind || kindFromName(draft.name)) === preset.kind}
                onSelectedChange={() => pickPreset(preset)}>
                {preset.label.replace(' pre-sale', '')}
              </Tag>
            ))}
          </div>
        </>
      ) : null}

      <div className="presale-window-editor-row">
        <div className="presale-window-editor-field">
          <Select label="Starts" required options={options(startChoices)}
            value={draft.startCustom ? CUSTOM : String(draft.start_show_activity_id || '')} onChange={onStart}
            hint={startError} />
          {draft.startCustom ? (
            <DatePicker label="Custom start" time value={customStart}
              onChange={(date) => patch({ start_time: date })} />
          ) : null}
        </div>
        <div className="presale-window-editor-field">
          <Select label="Ends" required options={options(endChoices)}
            value={draft.endCustom ? CUSTOM : String(draft.end_show_activity_id || '')} onChange={onEnd}
            hint={endError || (variant === 'public' && !endBefore ? undefined : HINTS[variant])} />
          {draft.endCustom ? (
            <DatePicker label="Custom end" time value={customEnd}
              onChange={(date) => patch({ end_time: date })} />
          ) : null}
        </div>
      </div>

      {presale ? (
        <>
          <div className="presale-window-editor-row">
            <TextInput label="Quantity" name="available" value={draft.available == null ? '' : String(draft.available)}
              onChange={(e) => patch({ available: e.target.value.replace(/[^0-9]/g, '') })}
              error={qtyError}
              hint={pool ? `Held from the ${pool} pool · blank shares the whole pool` : 'Blank shares the whole pool'} />
            {ticketType.free ? null : (
              <TextInput label="Price" name="price" leadingText="$"
                value={draft.price_two_decimals == null ? '' : draft.price_two_decimals}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  patch({ price_two_decimals: value === '' ? null : value });
                }}
                hint={`Blank = same as ticket ($${ticketType.base_price_two_decimals || '0.00'})`} />
            )}
          </div>

          <TextInput label="Access code" name="access_code"
            value={draft.access_code || ''}
            disabled={draft.access_code_managed === false}
            onChange={(e) => patch({
              access_code: [...e.target.value.toUpperCase()].filter((char) => PROMO_CODE_CHAR_PATTERN.test(char)).join(''),
            })}
            hint={draft.access_code_managed === false
              ? 'This window is unlocked by a code managed in Promo Codes'
              : 'Optional · fans need this code to see the pre-sale'} />
        </>
      ) : null}

      <div className="presale-window-editor-actions">
        {isNew || !presale ? <span /> : (
          <Tooltip title={toInt(draft.sold_count) > 0 ? 'This window has sales. Turn it off instead.' : null}>
            <span>
              <Button variant="destructive" disabled={toInt(draft.sold_count) > 0} onClick={() => onRemove(joined)}>
                Remove window
              </Button>
            </span>
          </Tooltip>
        )}
        <div className="presale-window-editor-actions-right">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" className="presale-window-editor-save" disabled={!valid}
            onClick={() => valid && onSave(joined)}>
            {isNew ? 'Add window' : 'Save window'}
          </Button>
        </div>
      </div>
    </div>
  );
};

PresaleWindowEditor.propTypes = {
  window: PropTypes.shape({ name: PropTypes.string }).isRequired,
  ticketType: PropTypes.shape({ available: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }).isRequired,
  showActivities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })).isRequired,
  isNew: PropTypes.bool,
  variant: PropTypes.oneOf([PRESALE, 'public', DAY_OF]),
  startAfter: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  endBefore: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

PresaleWindowEditor.defaultProps = { isNew: false, variant: PRESALE, startAfter: null, endBefore: null };

export default PresaleWindowEditor;
